//
// Copyright (c) 2019-Present Syncari All rights reserved.
//

import { message, Tag } from 'antd';
import ObjectID from 'bson-objectid';
import cx from 'classnames';
import { cloneDeep } from 'lodash/fp';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import AgTable from 'components/AgTable';
import Button from 'components/Button';
import { ReactComponent as ChevronDownIcon } from 'assets/icons/chevron-down.svg';
import { ReactComponent as CheckIcon } from 'assets/icons/success-check.svg';
import { ReactComponent as ChevronRightIcon } from 'assets/icons/chevron-right.svg';
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg';
import Modal from 'components/Modal';
import FieldTypeBadge from 'components/FieldTypeBadge';
import { useI18nContext, withI18n } from 'components/I18nProvider';
import InlineMessage from 'components/InlineMessage';
import { Divider, HStack, Stack } from 'components/layout';
import { ConfigFormValues } from 'components/skull';
import { FieldDataType } from 'components/types';
import { useEnhancedDispatch as useDispatch, useEnhancedSelector as useSelector } from 'hooks/redux';
import {
  makeCustomActionPayload,
  makeCustomActionPythonPayload,
} from 'pages/sync-studio/action-studio/CustomAction.util';
import { validateCustomAction } from 'store/custom-action/thunks';
import { CustomActionTestingResponse } from 'store/custom-action/types';
import {
  testCustomActionPython,
  CustomActionPythonTestingPayload,
  saveCustomActionPython,
} from 'store/custom-action-python';
import { HTTPCustomSynapseEntity } from 'store/custom-synapse/types';
import { selectCurrentInstanceId } from 'store/user/selectors';
import AppConstants from 'utils/AppConstants';
import { tNamespaced } from 'utils/i18nUtil';

// Module-level translator for cell renderers (outside component, no hook access)
const tl = tNamespaced('ActionSetup');

import ActionRequestResponse from './ActionRequestResponse';
import ActionPythonTestResponse from './ActionPythonTestResponse';
import { CustomAction } from './types';

import './ActionTesting.less';

const DefaultColDef = { flex: 1 };

const PlaceholderCellRenderer = ({ value, colDef }: { value: string; colDef: any }) => {
  if (!value) {
    return <span className="cell-placeholder">{colDef.cellEditorParams?.placeholder || tl('enter_value')}</span>;
  }
  return <span>{value}</span>;
};

const ParameterCellRenderer = ({ data }: { value: string; data: any }) => {
  const displayText = data.displayName || data.name;
  if (!displayText) {
    return <span className="cell-placeholder">{tl('enter_parameter_name')}</span>;
  }
  const dataType = (data.dataType || 'string').toLowerCase() as FieldDataType;
  return (
    <span className="parameter-cell">
      <FieldTypeBadge dataType={dataType} size="small" disableTooltip />
      <span>{displayText}</span>
    </span>
  );
};

export interface ActionMultipleTestingValue {
  testCases: TestCase[];
  savedPythonActionId?: string;
}

export interface TestCase {
  id: string;
  /** Backend-assigned ID, set after the first successful test run. Used to identify the test on subsequent runs. */
  persistedId?: string;
  name: string;
  description?: string;
  variableValues: VariableValue[];
  requestResponse?: CustomActionTestingResponse;
  createdAt: Date;
  isDefault?: boolean;
}

export interface VariableValue {
  id: string;
  name?: string;
  displayName?: string;
  value?: string;
  dataType?: string;
  required?: string;
}

export interface ActionMultipleTestingProps {
  className?: string;
  customAction?: CustomAction;
  defaultValue?: ActionMultipleTestingValue;
  onChange?: (value: ActionMultipleTestingValue) => void;
  readOnly?: boolean;
  httpSynapseEntity?: HTTPCustomSynapseEntity;
  formValues?: ConfigFormValues;
}

export const ActionMultipleTesting = ({
  className,
  customAction: customActionProp,
  defaultValue,
  onChange,
  readOnly,
  httpSynapseEntity,
  formValues,
}: ActionMultipleTestingProps) => {
  const { tn } = useI18nContext();
  const dispatch = useDispatch();
  const instanceId = useSelector(selectCurrentInstanceId);

  // Build customAction from formValues if available, otherwise use the prop
  const customAction = useMemo(() => {
    if (formValues) {
      // Check if this is a Python action by checking if pythonCode field exists in formValues
      // or if the customActionProp indicates it's a Python action
      const isPythonFromFormValues = 'pythonCode' in (formValues || {});
      const isPythonFromProp = customActionProp?.actionConfiguration?.method === 'python';

      if (isPythonFromFormValues || isPythonFromProp) {
        // For Python actions
        return {
          id: customActionProp?.id, // Preserve ID if exists
          displayName: formValues?.['displayName']?.value || '',
          apiName: formValues?.['apiName']?.value || '',
          description: formValues?.['description']?.value,
          basicHelpText: formValues?.['basicHelpText']?.value,
          helpLink: formValues?.['helpLink']?.value,
          tags: formValues?.['tags']?.value,
          iconPath: formValues?.['iconPath']?.value,
          pythonCode: formValues?.['pythonCode']?.value,
          pythonRequirements: formValues?.['pythonRequirements']?.value,
          variables: formValues?.['variables']?.value,
          actionConfiguration: {
            method: 'python',
            variables: formValues?.['variables']?.value,
          },
        } as any;
      }
      // For HTTP actions
      const actionConfiguration = formValues?.['actionConfiguration']?.['value'];
      return ({
        displayName: formValues?.['displayName']?.value || '',
        apiName: formValues?.['apiName']?.value || '',
        description: formValues?.['description']?.value,
        basicHelpText: formValues?.['basicHelpText']?.value,
        helpLink: formValues?.['helpLink']?.value,
        tags: formValues?.['tags']?.value,
        iconPath: formValues?.['iconPath']?.value,
        actionConfiguration: actionConfiguration as any,
      } as unknown) as CustomAction;
    }
    return customActionProp;
  }, [formValues, customActionProp]);

  const [testCases, setTestCases] = useState<TestCase[]>(() => {
    // If we have defaultValue with test cases, use those
    if (defaultValue?.testCases?.length) {
      return defaultValue.testCases;
    }

    // Otherwise create empty test case that will be filled by useEffect
    const defaultTestCase: TestCase = {
      id: ObjectID.generate(),
      name: tn('test_case_number', { number: 1 }),
      description: '',
      variableValues: [], // Start empty, will be filled by useEffect
      createdAt: new Date(),
      isDefault: true,
    };

    return [defaultTestCase];
  });

  // Track the ID of the Python action saved on first test run (POST → subsequent runs use PUT)
  const [savedPythonActionId, setSavedPythonActionId] = useState<string | undefined>(
    (defaultValue as any)?.savedPythonActionId || (customActionProp as any)?.id || undefined
  );

  const [expandedTestCases, setExpandedTestCases] = useState<Record<string, boolean>>({});

  const isExpanded = useCallback((testCaseId: string) => expandedTestCases[testCaseId] !== false, [expandedTestCases]);

  const toggleTestCase = useCallback((testCaseId: string) => {
    setExpandedTestCases((prev) => ({ ...prev, [testCaseId]: prev[testCaseId] === false }));
  }, []);

  const [runTestLabels, setRunTestLabels] = useState<Record<string, string>>({});
  const [markAsSuccessfulLabels, setMarkAsSuccessfulLabels] = useState<Record<string, string>>({});
  const [validationErrorMsgs, setValidateErrorMsgs] = useState<Record<string, string>>({});
  const [testingErrorMsgs, setTestingErrorMsgs] = useState<Record<string, string>>({});
  const [testSuccessStates, setTestSuccessStates] = useState<Record<string, boolean>>({});

  const isPythonAction = useMemo(() => {
    return customAction?.actionConfiguration?.method === 'python' || (customAction as any)?.pythonCode !== undefined;
  }, [customAction]);

  // Memoize variables to prevent unnecessary re-renders
  const variables = useMemo(() => {
    const vars =
      formValues?.['variables']?.value ||
      (customActionProp as any)?.variables ||
      customActionProp?.actionConfiguration?.variables;
    // Return the variables array, or empty array if undefined
    return vars && Array.isArray(vars) ? vars : [];
  }, [formValues?.['variables']?.value, customActionProp]);

  // Create a stable key based on variable structure to detect actual changes
  const variablesStructureKey = useMemo(() => {
    if (!variables || variables.length === 0) {
      return 'empty';
    }
    return JSON.stringify(
      variables.map((v: any) => ({
        name: v.name,
        displayName: v.displayName,
        dataType: v.dataType,
      }))
    );
  }, [variables]);

  useEffect(() => {
    const initialLabels: Record<string, string> = {};
    const initialMarkLabels: Record<string, string> = {};
    testCases.forEach((testCase) => {
      initialLabels[testCase.id] = tn('run_test_now');
      initialMarkLabels[testCase.id] = tn('mark_as_successful');
    });
    setRunTestLabels(initialLabels);
    setMarkAsSuccessfulLabels(initialMarkLabels);
  }, [testCases, tn]);

  useEffect(() => {
    onChange?.({
      testCases,
      savedPythonActionId,
    });
  }, [onChange, testCases, savedPythonActionId]);

  // Track if we've ever had variables to prevent clearing data
  const hasHadVariablesRef = useRef(false);

  // Update testCases only when variable structure actually changes
  useEffect(() => {
    // If variables are empty, clear variableValues from test cases
    if (variablesStructureKey === 'empty' || !variables || variables.length === 0) {
      setTestCases((prev) =>
        prev.map((testCase) => ({
          ...testCase,
          variableValues: [],
        }))
      );
      return;
    }

    // Mark that we've had variables at least once
    hasHadVariablesRef.current = true;

    setTestCases((prev) =>
      prev.map((testCase) => {
        // If we have no variableValues yet, create them
        if (!testCase.variableValues || testCase.variableValues.length === 0) {
          const newVariableValues = variables.map((variable: any) => ({
            id: ObjectID.generate(),
            name: variable.name,
            displayName: variable.displayName,
            dataType: variable.dataType,
            required: variable.required,
            value: '',
          }));
          return {
            ...testCase,
            variableValues: newVariableValues,
          };
        }

        // Update existing variableValues, preserving user-entered values
        const newVariableValues = variables.map((variable: any) => {
          const existingValue = testCase.variableValues?.find((v) => v.name === variable.name);
          return {
            id: existingValue?.id || ObjectID.generate(),
            name: variable.name,
            displayName: variable.displayName,
            dataType: variable.dataType,
            required: variable.required,
            value: existingValue?.value || '',
          };
        });

        return {
          ...testCase,
          variableValues: newVariableValues,
        };
      })
    );
  }, [variablesStructureKey, variables]);

  const createTestCase = useCallback(() => {
    // Get variables from existing test case if available (to handle case where variables become empty temporarily)
    const referenceVariables = testCases[0]?.variableValues || variables;

    const newTestCase: TestCase = {
      id: ObjectID.generate(),
      name: tn('test_case_number', { number: testCases.length + 1 }),
      description: '',
      variableValues:
        referenceVariables?.map((variable: any) => ({
          id: ObjectID.generate(),
          name: variable.name,
          displayName: variable.displayName,
          dataType: variable.dataType,
          required: variable.required,
          value: '',
        })) || [],
      createdAt: new Date(),
    };

    setTestCases((prev) => [...prev, newTestCase]);
    setRunTestLabels((prev) => ({ ...prev, [newTestCase.id]: tn('run_test_now') }));
    setMarkAsSuccessfulLabels((prev) => ({ ...prev, [newTestCase.id]: tn('mark_as_successful') }));
  }, [testCases, variables, tn]);

  const deleteTestCase = useCallback((testCaseId: string) => {
    Modal.confirm({
      title: tn('delete_test_case'),
      content: tn('delete_test_case_confirm'),
      onOk: () => {
        setTestCases((prev) => prev.filter((tc) => tc.id !== testCaseId));
        setRunTestLabels((prev) => {
          const newLabels = { ...prev };
          delete newLabels[testCaseId];
          return newLabels;
        });
        setMarkAsSuccessfulLabels((prev) => {
          const newLabels = { ...prev };
          delete newLabels[testCaseId];
          return newLabels;
        });
        setValidateErrorMsgs((prev) => {
          const newMsgs = { ...prev };
          delete newMsgs[testCaseId];
          return newMsgs;
        });
        setTestingErrorMsgs((prev) => {
          const newMsgs = { ...prev };
          delete newMsgs[testCaseId];
          return newMsgs;
        });
        setTestSuccessStates((prev) => {
          const newStates = { ...prev };
          delete newStates[testCaseId];
          return newStates;
        });
      },
    });
  }, []);

  const onRowEditingStopped = useCallback((evt: any, testCaseId: string) => {
    if (evt?.data) {
      const { data } = evt;
      setTestCases((prev) =>
        prev?.map((testCase) =>
          testCase.id === testCaseId
            ? {
                ...testCase,
                variableValues: testCase.variableValues.map((variableValue) =>
                  variableValue.id === data.id ? data : variableValue
                ),
              }
            : testCase
        )
      );
    }
  }, []);

  const runTest = useCallback(
    (testCaseId: string) => {
      const testCase = testCases.find((tc) => tc.id === testCaseId);
      if (!testCase) return;

      const variableValues = testCase.variableValues;

      // Validate: required variable values must be non-empty
      const hasEmptyValues = variableValues?.some(
        (v) => v.name && v.required !== AppConstants.FALSE && !v.value?.trim()
      );
      if (hasEmptyValues) {
        setValidateErrorMsgs((prev) => ({ ...prev, [testCaseId]: tn('all_variables_required') }));
        return;
      }
      setValidateErrorMsgs((prev) => ({ ...prev, [testCaseId]: '' }));

      if (customAction) {
        setTestingErrorMsgs((prev) => ({ ...prev, [testCaseId]: '' }));
        setTestSuccessStates((prev) => ({ ...prev, [testCaseId]: false }));
        setRunTestLabels((prev) => ({ ...prev, [testCaseId]: tn('preparing_test') }));

        if (isPythonAction) {
          // Python Action Testing
          if (!instanceId) {
            setValidateErrorMsgs((prev) => ({ ...prev, [testCaseId]: tn('instance_id_required') }));
            setRunTestLabels((prev) => ({ ...prev, [testCaseId]: tn('run_test_now') }));
            return;
          }

          // Use the saved ID from a previous test run (PUT) or fall back to the action's own ID
          const functionId = savedPythonActionId || (customAction as any).id;

          // For the first run (no ID yet): generate stable placeholder names.
          // For subsequent runs (has ID): pass empty so the backend merge preserves the existing names.
          const timestamp = Date.now();
          const displayName = functionId
            ? customAction.displayName || ''
            : customAction.displayName || `Python Action ${timestamp}`;
          const apiName = functionId
            ? customAction.apiName || ''
            : customAction.apiName || `python_action_${timestamp}`;

          // Step 1: Convert all testCases to CustomFunctionTest format
          const tests = testCases.map((tc) => {
            const inputData: Record<string, any> = {};
            tc.variableValues?.forEach((val) => {
              if (val.name) {
                inputData[val.name] = val.value;
              }
            });

            return {
              id: tc.persistedId,
              inputData: inputData,
            };
          });

          // Step 2: Build the save payload using makeCustomActionPythonPayload
          const basePayload = makeCustomActionPythonPayload(
            {
              displayName: displayName,
              apiName: apiName,
              description: customAction.description || '',
              basicHelpText: customAction.basicHelpText || '',
              tags: customAction.tags || [],
              pythonCode: (customAction as any).pythonCode,
              pythonRequirements: (customAction as any).pythonRequirements,
              variables: (customAction as any).variables,
              iconPath: customAction.iconPath,
            },
            functionId
          );

          const savePayload = {
            ...basePayload,
            tests: tests,
            instanceId: instanceId,
          };

          dispatch(saveCustomActionPython(savePayload))
            .then((saveResult: any) => {
              if (saveResult.meta.requestStatus === 'rejected') {
                setTestingErrorMsgs((prev) => ({ ...prev, [testCaseId]: saveResult.payload?.message }));
                setTestSuccessStates((prev) => ({ ...prev, [testCaseId]: false }));
                setRunTestLabels((prev) => ({ ...prev, [testCaseId]: tn('run_test_now') }));
                return;
              }

              // Step 3: Get the function ID and test ID from the saved result
              const savedFunctionId = saveResult.payload?.id;
              const savedTests = saveResult.payload?.tests || [];
              const testIndex = testCases.findIndex((tc) => tc.id === testCaseId);
              const savedTest = savedTests[testIndex];

              // Persist the function ID so subsequent runs use PUT instead of POST
              if (savedFunctionId && !savedPythonActionId) {
                setSavedPythonActionId(savedFunctionId);
              }

              if (!savedFunctionId) {
                setTestingErrorMsgs((prev) => ({
                  ...prev,
                  [testCaseId]: tn('failed_get_function_id'),
                }));
                setTestSuccessStates((prev) => ({ ...prev, [testCaseId]: false }));
                setRunTestLabels((prev) => ({ ...prev, [testCaseId]: tn('run_test_now') }));
                return;
              }

              if (!savedTest || !savedTest.id) {
                setTestingErrorMsgs((prev) => ({ ...prev, [testCaseId]: tn('failed_save_test_case') }));
                setTestSuccessStates((prev) => ({ ...prev, [testCaseId]: false }));
                setRunTestLabels((prev) => ({ ...prev, [testCaseId]: tn('run_test_now') }));
                return;
              }

              // Step 4: Execute the test with the backend IDs
              // Convert variableValues to array format for test execution
              const variableValuesArray =
                variableValues?.map((val) => ({
                  name: val.name,
                  value: val.value,
                })) || [];

              const pythonPayload: CustomActionPythonTestingPayload = {
                variableValues: variableValuesArray,
                instanceId: instanceId,
                functionId: savedFunctionId,
                testId: savedTest.id,
              };

              setRunTestLabels((prev) => ({ ...prev, [testCaseId]: tn('running') }));

              dispatch(testCustomActionPython(pythonPayload)).then((result) => {
                const payload = result.payload as any;
                const isPayloadError =
                  result.meta.requestStatus === 'rejected' ||
                  (payload?.error && typeof payload?.status === 'number' && payload.status >= 400);

                if (isPayloadError) {
                  const errorMsg = result.meta.requestStatus === 'rejected' ? payload?.message : payload?.message;
                  setTestingErrorMsgs((prev) => ({ ...prev, [testCaseId]: errorMsg }));
                  setTestSuccessStates((prev) => ({ ...prev, [testCaseId]: false }));
                  setRunTestLabels((prev) => ({ ...prev, [testCaseId]: tn('run_test_now') }));
                } else {
                  setTestCases((prev) =>
                    prev.map((tc) =>
                      tc.id === testCaseId ? { ...tc, persistedId: savedTest.id, requestResponse: result.payload } : tc
                    )
                  );
                  setTestSuccessStates((prev) => ({ ...prev, [testCaseId]: true }));
                  setRunTestLabels((prev) => ({ ...prev, [testCaseId]: tn('run_test_now') }));
                }
              });
            })
            .catch((error) => {
              setTestingErrorMsgs((prev) => ({
                ...prev,
                [testCaseId]: tn('failed_save_custom_action', { error: error.message || error }),
              }));
              setTestSuccessStates((prev) => ({ ...prev, [testCaseId]: false }));
              setRunTestLabels((prev) => ({ ...prev, [testCaseId]: tn('run_test_now') }));
            });
        }
      }
    },
    [customAction, dispatch, tn, testCases, httpSynapseEntity, isPythonAction, instanceId, savedPythonActionId]
  );

  const markAsSuccessful = useCallback(
    (testCaseId: string) => {
      const testCase = testCases.find((tc) => tc.id === testCaseId);
      if (!testCase || !customAction) return;

      setValidateErrorMsgs((prev) => ({ ...prev, [testCaseId]: '' }));
      setMarkAsSuccessfulLabels((prev) => ({ ...prev, [testCaseId]: tn('validation_in_progress') }));

      if (isPythonAction) {
        // Python actions don't need validation
        return;
      } else {
        // HTTP Action Validation
        const customActionPayload = makeCustomActionPayload(customAction);
        dispatch(validateCustomAction(customActionPayload)).then((result) => {
          if (result.meta.requestStatus === 'rejected') {
            setValidateErrorMsgs((prev) => ({ ...prev, [testCaseId]: result.payload?.message }));
          } else {
            message.success(tn('validation_successful'));
          }
          setMarkAsSuccessfulLabels((prev) => ({ ...prev, [testCaseId]: tn('mark_as_successful') }));
        });
      }
    },
    [customAction, dispatch, tn, testCases, isPythonAction]
  );

  const suppressKeyboard = useCallback(() => true, []);

  const columns = useMemo(() => {
    return [
      {
        headerName: tn('parameter'),
        field: 'displayName',
        editable: false,
        suppressKeyboardEvent: suppressKeyboard,
        suppressMovable: true,
        resizable: true,
        flex: 1,
        cellRendererFramework: ParameterCellRenderer,
      },
      {
        headerName: tn('test_value'),
        field: 'value',
        editable: !readOnly,
        suppressKeyboardEvent: suppressKeyboard,
        suppressMovable: true,
        resizable: true,
        flex: 3,
        cellRendererFramework: PlaceholderCellRenderer,
        cellClass: 'test-value-cell',
        cellEditorParams: {
          placeholder: tn('enter_test_value'),
        },
      },
    ];
  }, [tn, readOnly, suppressKeyboard]);

  return (
    <Stack className={cx('synri-action-multiple-testing', className)} spacing="md">
      <HStack justify="space-between" align="center" className="multiple-testing-header">
        <div>
          <h3>{tn('testing')}</h3>
        </div>
        <Button onClick={createTestCase} disabled={readOnly}>
          <PlusIcon className="synri-action-btn-icon" /> {tn('new_test_case')}
        </Button>
      </HStack>
      {testCases.map((testCase) => {
        const isThisTestRunning = !!runTestLabels[testCase.id] && runTestLabels[testCase.id] !== tn('run_test_now');
        const hasRun = testSuccessStates[testCase.id] || !!testCase.requestResponse;
        const runBtnLabel = hasRun ? tn('run_test_again') : runTestLabels[testCase.id] || tn('run_test_now');
        const expanded = isExpanded(testCase.id);

        return (
          <div key={testCase.id} className="test-case-container">
            <div className="test-case-header">
              <div className="test-case-header-left">
                <h4>{testCase.name}</h4>
                {!testSuccessStates[testCase.id] && !testCase.requestResponse && !testingErrorMsgs[testCase.id] && (
                  <Tag className="test-case-status-badge test-case-not-run-badge">{tn('not_run_yet')}</Tag>
                )}
                {testSuccessStates[testCase.id] && (
                  <Tag color="green" className="test-case-status-badge">
                    {tn('successful')}
                  </Tag>
                )}
                {!!testingErrorMsgs[testCase.id] && !testSuccessStates[testCase.id] && (
                  <Tag color="red" className="test-case-status-badge">
                    {tn('test_failed')}
                  </Tag>
                )}
              </div>
              <div className="test-case-header-right">
                {testCases.length > 1 && (
                  <Button size="small" onClick={() => deleteTestCase(testCase.id)} disabled={readOnly}>
                    {tn('delete')}
                  </Button>
                )}
                <button className="test-case-collapse-toggle" onClick={() => toggleTestCase(testCase.id)}>
                  <ChevronDownIcon
                    className={cx('synri-action-collapse-icon', { 'synri-action-collapse-icon--expanded': expanded })}
                  />
                </button>
              </div>
            </div>

            {expanded && (
              <>
                {validationErrorMsgs[testCase.id] && (
                  <InlineMessage
                    type={'error'}
                    className="synri-action-testing-error-msg"
                    title={validationErrorMsgs[testCase.id]}>
                    {validationErrorMsgs[testCase.id]}
                  </InlineMessage>
                )}
                {testCase.variableValues && testCase.variableValues.length > 0 ? (
                  <AgTable
                    defaultColDef={DefaultColDef}
                    suppressCellSelection
                    columnDefs={columns}
                    rowData={cloneDeep(testCase.variableValues || [])}
                    editType="fullRow"
                    stopEditingWhenGridLosesFocus
                    singleClickEdit
                    onRowEditingStopped={(evt) => onRowEditingStopped(evt, testCase.id)}
                  />
                ) : (
                  <div className="no-variables-message">
                    <p>{tn('no_variables_defined')}</p>
                  </div>
                )}
                <div className="test-case-actions">
                  <Button
                    type="default"
                    className="run-test-button"
                    loading={isThisTestRunning}
                    onClick={() => runTest(testCase.id)}
                    disabled={readOnly}>
                    {!isThisTestRunning && (
                      <ChevronRightIcon className="synri-action-btn-icon" width={12} height={12} />
                    )}
                    {runBtnLabel}
                  </Button>
                </div>

                {testingErrorMsgs[testCase.id] && !testCase.requestResponse && (
                  <div className="test-case-results">
                    <Divider />
                    <InlineMessage
                      type={'error'}
                      className="synri-action-testing-error-msg"
                      title={testingErrorMsgs[testCase.id]}>
                      {testingErrorMsgs[testCase.id]}
                    </InlineMessage>
                  </div>
                )}

                {(testCase.requestResponse || isThisTestRunning) && (
                  <div className="test-case-results">
                    <Divider />
                    {isThisTestRunning && !testCase.requestResponse ? (
                      <InlineMessage type={'info'} className="synri-action-testing-running-msg">
                        {tn('testing_in_progress')}
                      </InlineMessage>
                    ) : isPythonAction ? (
                      <ActionPythonTestResponse
                        errorMsg={testingErrorMsgs[testCase.id]}
                        testResponse={testCase.requestResponse}
                      />
                    ) : (
                      <ActionRequestResponse
                        errorMsg={testingErrorMsgs[testCase.id]}
                        requestResponse={testCase.requestResponse}
                      />
                    )}
                    {testSuccessStates[testCase.id] && !httpSynapseEntity && !isPythonAction && (
                      <HStack className="synri-mark-as-successful-button-container">
                        <Button
                          type="default"
                          className="synri-mark-as-successful-button"
                          loading={markAsSuccessfulLabels[testCase.id] !== tn('mark_as_successful')}
                          onClick={() => markAsSuccessful(testCase.id)}
                          disabled={readOnly}>
                          <CheckIcon className="synri-action-btn-icon" />
                          {markAsSuccessfulLabels[testCase.id] || tn('mark_as_successful')}
                        </Button>
                      </HStack>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}
    </Stack>
  );
};

export default withI18n(ActionMultipleTesting, 'ActionSetup');
